import { createFileRoute } from "@tanstack/react-router";
import { SiteLayout } from "@/components/site/SiteLayout";
import { seo } from "@/lib/seo";

export const Route = createFileRoute("/certifications")({
  head: () => ({
    meta: [
      ...seo({
        title: "Quality Assurance – QT Foods Wholesale",
        description:
          "Certifications (HACCP, USDA Organic, JAS, KOSHER) and quality control processes",
      }),
    ],
  }),
  component: Page,
});

function Page() {
  return (
    <SiteLayout breadcrumbs={[{ label: "Certifications" }]}>
      <section className="mx-auto max-w-4xl px-5 pt-12 pb-24 lg:px-8 lg:pt-16">
        <h1 className="font-serif text-5xl md:text-6xl leading-tight">
          Certifications & Quality Assurance
        </h1>

        <div className="mt-16 space-y-12">
          <div>
            <h2 className="text-2xl font-medium tracking-tight mb-4">
              Global Standards, Local Integrity
            </h2>
            <p className="text-lg text-muted-foreground leading-relaxed">
              To serve as a reliable partner in the global food industry, the production facilities
              of our trusted manufacturing partners adhere to the highest international benchmarks
              for sanitation and ethical production.
            </p>
          </div>

          <div>
            <h2 className="text-2xl font-medium tracking-tight mb-4">Certification</h2>
            <p className="text-lg text-muted-foreground leading-relaxed">
              Through our trusted manufacturing partners, we maintain management systems aligned
              with a range of domestic and international certification standards, reflecting our
              responsibility for safety and quality. By meeting third-party requirements at every
              stage—from raw material intake to production and shipment—we provide tea that
              customers in Japan and overseas can use with confidence.
            </p>
          </div>
        </div>

        <div className="mt-20 grid gap-12 md:grid-cols-2">
          {/* USDA Organic */}
          <div className="flex gap-6">
            <div className="w-24 shrink-0">
              <img
                src="/assets/images/USDA-organic.svg"
                alt="USDA Organic"
                width={96}
                height={96}
                className="w-full object-contain"
              />
            </div>
            <div>
              <h3 className="font-semibold text-lg mb-2">USDA Organic</h3>
              <p className="text-sm text-muted-foreground leading-relaxed">
                Our partner facilities maintain USDA Organic certification, which guarantees our
                teas are grown and processed without prohibited synthetic fertilizers or pesticides,
                ensuring full compliance with strict US organic standards for our North American
                clients.
              </p>
            </div>
          </div>

          {/* JAS Organic */}
          <div className="flex gap-6">
            <div className="w-24 shrink-0">
              <img
                src="/assets/images/jas-organic.svg"
                alt="JAS Organic"
                width={96}
                height={96}
                className="w-full object-contain"
              />
            </div>
            <div>
              <h3 className="font-semibold text-lg mb-2">JAS Organic</h3>
              <p className="text-sm text-muted-foreground leading-relaxed">
                Our partner facilities operate under the strict Japanese Agricultural Standard (JAS)
                for organic foods. This rigorous domestic certification ensures the highest level of
                organic agricultural practices are followed, from the Japanese tea gardens through
                the entire production process.
              </p>
            </div>
          </div>

          {/* KOSHER */}
          <div className="flex gap-6">
            <div className="w-24 shrink-0">
              <img
                src="/assets/images/kosher.svg"
                alt="KOSHER Certification"
                width={96}
                height={96}
                className="w-full object-contain"
              />
            </div>
            <div>
              <h3 className="font-semibold text-lg mb-2">KOSHER Certification</h3>
              <p className="text-sm text-muted-foreground leading-relaxed">
                A certification based on Jewish dietary laws, examining raw materials, production
                processes, and facility management. Our processing partners are certified to meet
                Kosher requirements, enabling us to serve markets where religious considerations are
                essential.
              </p>
            </div>
          </div>

          {/* FDA Registration */}
          <div className="flex gap-6">
            <div className="w-24 shrink-0 flex items-center justify-center">
              <img
                src="/assets/images/FDA.svg"
                alt="FDA Registration"
                width={96}
                height={96}
                className="w-full object-contain"
              />
            </div>
            <div>
              <h3 className="font-semibold text-lg mb-2">FDA Registration</h3>
              <p className="text-sm text-muted-foreground leading-relaxed">
                The production factories we partner with are registered with the U.S. Food and Drug
                Administration (FDA) in compliance with regulations required for food exports to the
                United States. With established manufacturing and hygiene controls, we ensure
                reliable product supply for overseas markets.
              </p>
            </div>
          </div>

          {/* Halal Certification */}
          <div className="flex gap-6">
            <div className="w-24 shrink-0 flex items-center justify-center">
              <img
                src="/assets/images/npo-halal-japan.png"
                alt="Halal Certification"
                width={96}
                height={96}
                className="w-full object-contain"
              />
            </div>
            <div>
              <h3 className="font-semibold text-lg mb-2">Halal Certification</h3>
              <p className="text-sm text-muted-foreground leading-relaxed">
                A certification guaranteeing that products are prepared in accordance with Islamic dietary laws. Our certified partners ensure strict compliance in processing and facility management, enabling us to confidently serve markets requiring Halal standards.
              </p>
            </div>
          </div>
        </div>
      </section>
    </SiteLayout>
  );
}
